import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import type { HelpTicketKind } from "@backstory/protocol";
import { Dialog, Field, useToast } from "../../components/ui";
import { helpApi, helpKeys } from "../staff/api";
import { ImagePicker, pastedImages, plainTextIssue, takeImages } from "./images";

const KINDS: { id: HelpTicketKind; label: string }[] = [
  { id: "bug", label: "Bug" },
  { id: "feature", label: "Feature request" },
  { id: "question", label: "Question" },
];

const COPY: Record<HelpTicketKind, {
  title: string;
  subtitle: string;
  summary: string;
  summaryPlaceholder: string;
  detail: string;
  detailPlaceholder: string;
  aboutPage: string;
  send: string;
}> = {
  bug: {
    title: "Report a bug",
    subtitle: "Tell us what broke. This goes to the Backstory team.",
    summary: "What's broken?",
    summaryPlaceholder: "The invite button does nothing",
    detail: "What happened?",
    detailPlaceholder: "What you expected, what you saw, and how to reproduce it.",
    aboutPage: "This happened on the page I am on",
    send: "Report bug",
  },
  feature: {
    title: "Request a feature",
    subtitle: "Tell us what you need. This goes to the Backstory team.",
    summary: "What should we add?",
    summaryPlaceholder: "Invite someone with a link",
    detail: "How would this help?",
    detailPlaceholder: "Who needs it, and what they should be able to do.",
    aboutPage: "This is about the page I am on",
    send: "Request feature",
  },
  question: {
    title: "Ask a question",
    subtitle: "Ask the Backstory team.",
    summary: "Your question",
    summaryPlaceholder: "How do I invite a member?",
    detail: "Anything else we should know?",
    detailPlaceholder: "What you already tried, and what you are trying to do.",
    aboutPage: "This is about the page I am on",
    send: "Ask",
  },
};

/** A replay only exists on a session page. Elsewhere a session id has nothing to attach to. */
function sessionOnPage(path: string): string | undefined {
  const m = path.match(/^\/sessions\/([^/]+)/);
  return m?.[1];
}

/** Report a problem, a feature, or a question. SaaS only. */
export function ReportDialog({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [kind, setKind] = useState<HelpTicketKind>("bug");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [aboutPage, setAboutPage] = useState(true);
  const pageSession = sessionOnPage(path);
  const [includeSession, setIncludeSession] = useState(!!pageSession);
  const [images, setImages] = useState<File[]>([]);
  const [imageError, setImageError] = useState<string>();
  const copy = COPY[kind];
  const titleIssue = plainTextIssue(title);
  const bodyIssue = plainTextIssue(body);

  const create = useMutation({
    mutationFn: () =>
      helpApi.create(orgId, {
        kind,
        title: title.trim(),
        body: body.trim(),
        pagePath: aboutPage ? path : undefined,
        pageUrl: aboutPage ? window.location.href : undefined,
        sessionId: aboutPage && includeSession ? pageSession : undefined,
      }, images),
    onSuccess: (detail) => {
      void qc.invalidateQueries({ queryKey: helpKeys.list(orgId) });
      toast.push("success", "Request sent");
      onClose();
      setImages([]);
      void navigate({ to: "/help/$id", params: { id: detail.ticket.id } });
    },
    onError: (e: Error) => toast.push("error", e.message),
  });

  return createPortal(
    <Dialog
      open
      onClose={onClose}
      title={copy.title}
      subtitle={copy.subtitle}
      width={760}
      className="report-dialog"
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button type="submit" form="report-form" className="btn primary" disabled={create.isPending || !title.trim() || !body.trim() || !!titleIssue || !!bodyIssue || !!imageError}>
            {create.isPending ? "Sending…" : copy.send}
          </button>
        </>
      }
    >
      <form
        id="report-form"
        className="report-form"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="seg" role="group" aria-label="Request type">
          {KINDS.map((k) => (
            <button key={k.id} type="button" className={kind === k.id ? "seg-on" : ""} aria-pressed={kind === k.id} onClick={() => setKind(k.id)}>
              {k.label}
            </button>
          ))}
        </div>
        <Field label={copy.summary} error={titleIssue}>
          {(p) => <input {...p} className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={copy.summaryPlaceholder} required maxLength={200} />}
        </Field>
        <Field label={copy.detail} error={bodyIssue}>
          {(p) => (
            <textarea
              {...p}
              className="input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onPaste={(e) => {
                const pasted = pastedImages(e);
                if (pasted.length === 0) return;
                e.preventDefault();
                const next = takeImages(images, pasted);
                setImages(next.files);
                setImageError(next.error);
              }}
              placeholder={copy.detailPlaceholder}
              required
              maxLength={8000}
              rows={5}
            />
          )}
        </Field>
        <ImagePicker files={images} error={imageError} onChange={(next, err) => { setImages(next); setImageError(err); }} />
        <label className="check">
          <input type="checkbox" autoComplete="off" checked={aboutPage} onChange={(e) => setAboutPage(e.target.checked)} />
          <span>{copy.aboutPage} <span className="muted mono">{path}</span></span>
        </label>
        {aboutPage && pageSession && (
          <label className="check">
            <input type="checkbox" autoComplete="off" checked={includeSession} onChange={(e) => setIncludeSession(e.target.checked)} />
            <span>Include this session <span className="muted mono">{pageSession}</span></span>
          </label>
        )}
      </form>
    </Dialog>,
    document.body,
  );
}
